// Phase 4.2 — Create/Edit Bill screen, migrated to shadcn/Tailwind.
//
// UI only. Every piece of state, every effect and every handler below is the
// legacy one — dispatches (createBillV2Action, getProductItemAction,
// updateProductListAction, updateItemQuantity, removedProductListAction,
// getInvoiceDetailsAction, getCustomerListAction, getReferenceDataAction,
// clearProductItemListAction / clearSelectAction) and their payloads are
// unchanged. Only the rendering was reworked: MUI Grid/Select/TextField/Table +
// antd AutoComplete/InputNumber/DatePicker/Checkbox/Modal/Form are replaced by
// shadcn Card/Select/Input/Checkbox + a plain table, native date input, and
// ConfirmDialog. The antd <Form> wrapper is dropped because it never gated submit
// (validation is the existing `validation` flag + inline messages). `message.*`
// → sonner `toast.*`; SpinLoader → LoadingOverlay.
//
// Deviation from the plan's "Stepper": the legacy flow is a single page (customer
// details → add items → totals/save), not a wizard, and its totals are computed
// live during render off `productList`. Forcing a multi-step wizard would reshape
// that interdependent state, so this keeps the single-page flow in sectioned
// Cards — lower risk, same behavior.
import AppConstant from "../Globals/AppConstants"
import ApparelMenu from "./SubComponents/apparelMenu"
import { Pencil, Trash2, Plus, RotateCcw, Save, Search, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearProductItemListAction, clearSelectAction, createBillV2Action, employeeDropDownListAction, getBillNavigationRowAction, getCustomerListAction, getInvoiceDetailsAction, getProductItemAction, getReferenceDataAction, getSalesReturnBillDetailsAction, removedProductListAction, updateItemQuantity, updateProductListAction } from "../Store/actions/apparelAction";
import { useEffect, useRef, useState } from "react";
import { deepCopyFunction, isArrayNotEmpty, computeRoundedTotal, isValidMobileNumber } from "../Utils/helpers";
import { toast } from "sonner";
import AppImages from "../Globals/AppImages";
import { LoadingOverlay } from "../shared/loading-overlay";
import { ConfirmDialog } from "../shared/confirm-dialog";
import { FormSection, FieldRow } from "../shared/form-section";
import { Card, CardContent } from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Checkbox } from "ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { StatusBadge } from "../shared/status-badge";
import BillNavigator from "./orders/BillNavigator";
import useBillNavigation from "./orders/useBillNavigation";

const CreateBill = () => {
    const navigate = useNavigate();
    const [spinnerLoad, setSpinnerLoad] = useState(false);
    let amount: any = 0;
    let gst: any = 0;
    let cgst: any = 0;
    let sgst: any = 0;
    let totalAmount: any = 0;
    let number: any = 0;
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const [validation, setValidation] = useState(false);
    const [nilValidation, setNilValidation] = useState(false);
    // True once an existing customer has been picked (via either suggestion
    // list, or by typing a mobile number that fully matches one) — both
    // Customer Name and Mobile Number lock to prevent accidental edits.
    // Cleared back to false (both fields editable again) via the clear (X)
    // button on either field.
    const [customerSelected, setCustomerSelected] = useState(false);
    const [mobilenumberList, setMobileNumberList] = useState<any>(null);
    const [createBilOnload, setCreateBilOnload] = useState(false);
    const locationProps = useLocation().state;
    // Nil-customer bills stay fully locked in Edit Bill — no editing, no
    // suggestions, no clear button. This must match the backend's own
    // definition of a nil bill exactly (saveBillingDetails_v2's isNilBill:
    // BOTH customerName and mobileNumber are nil markers) — a real customer
    // who simply has no phone number on file (name set, mobile shows the
    // "Nil" placeholder) is NOT a nil bill and must stay editable.
    const isNilBill = locationProps?.isEdit == 1 &&
        String(locationProps?.data?.customerName ?? "").toLowerCase() === "nil" &&
        String(locationProps?.data?.mobileNumberList ?? "").toLowerCase() === "nil";
    // Business rule: a bill is fully editable only on the same calendar date
    // it was billed. Once that date is earlier than today, editing must be
    // locked to just the customer's contact details (name/mobile — handled
    // separately above via customerLocked/clearCustomerSelection) and the
    // Payment Method field; every other field below renders disabled. Keyed
    // off the bill's ORIGINAL date from locationProps, not the (possibly
    // still-editable-looking) createBillData.purchaseDate state, so this
    // can't be defeated by whatever the date field currently shows.
    // Mirrors BillingService.isBillDateRestrictedForEdit on the server,
    // which enforces the same rule independently.
    const originalBillDateStr = locationProps?.data?.date ? String(locationProps.data.date).slice(0, 10) : null;
    const isRestrictedEdit = locationProps?.isEdit == 1 &&
        originalBillDateStr != null &&
        originalBillDateStr !== new Date().toISOString().slice(0, 10);
    const [scannedId, setScannedId] = useState<any>(null);
    const [productApiLoad, setProductApiLoad] = useState(false)
    const [productListArray, setProductListArray] = useState<any>([])

    const { productItemList,
        productItemListOnload,
        productList,
        productItemListData,
        totalDiscount,
        paymentTypeData,
        billTypeData,
        createBillReducerOnload,
        createBillReducerOnload_V2,
        createBillReducerData_V2,
        customerListOnload,
        customerListData,
        getInvoiceDetailsData,
        getInvoiceDetailsOnload,
        genEmployeeDropDownListData,
        getSalesReturnBillDetailsData,
        billNavigationRow,
        billNavigationRowOnload
    } = apparelReducerState;
    console.log("reducer productList", productList)
    const paymentList = paymentTypeData?.getReferenceData;
    const billList = billTypeData?.getReferenceData;
    const employeeList = genEmployeeDropDownListData?.getEmployeeDropDownList;
    // Read-only "Returned items" panel data — each line's returned qty is
    // billed quantity minus quantityLeft (getBillReturnDetails already nets
    // out every non-deleted tbl_salesreturn row for this bill/product).
    const returnedLineItems = (getSalesReturnBillDetailsData?.getSalesReturnBillDetails?.items || [])
        .filter((item: any) => Number(item?.quantity || 0) - Number(item?.quantityLeft ?? item?.quantity ?? 0) > 0)
        .map((item: any) => ({
            productId: item?.productId,
            productName: item?.productName,
            returnedQty: Number(item?.quantity || 0) - Number(item?.quantityLeft ?? item?.quantity ?? 0),
            status: item?.status
        }));
    // Per-product minimum quantity derived from sales returns — used both
    // for inline validation (quantity input clamping) and save-time
    // validation in createBillApi().
    const returnedQtyMap = new Map<string, number>();
    returnedLineItems.forEach((item: any) => {
        returnedQtyMap.set(item.productId, (returnedQtyMap.get(item.productId) || 0) + item.returnedQty);
    });
    const [searchNumber, setSearchnumber] = useState("");
    const [customerListLoad, setCustomerListLoad] = useState(false);
    const fetchRef = useRef(0);
    const createBillTemp = {
        billingId: "",
        customerId: null,
        billTypeId: null,
        customerName: "",
        mobileNumber: "",
        paymentMethodRefId: 1,
        purchaseDate: new Date().toISOString().slice(0, 10),
        billedByEmployeeId: null,
        productItemId: null,
        individualAmount: null,
        gst: null,
        individualDiscount: null,
        totalAmount: null,
        roundOff: 0,
        items: null,
        quantity: "",
        multipleCount: null
    }
    const [createBillData, setCreateBillData] = useState<any>(deepCopyFunction(createBillTemp));
    const [availableCount, setAvailableCount] = useState<any>(null);
    const [getInvoiceDetailsload, setGetInvoiceDetailsload] = useState<any>(false)
    const [removeProductmodal, setRemoveProductmodal] = useState(false)
    const [removedData, setRemovedData] = useState<any>(null)
    const [discountEditIndex, setDiscountEditIndex] = useState<any>(null);
    const [quantityEditIndex, setQuantityEditIndex] = useState<any>(null);
    const [discountEditData, setDiscountEditData] = useState<any>(null)
    // Governs how a typed line-discount number is interpreted at entry time only.
    // Everything downstream (productList[].discount, productItemListData[].discountPercentage,
    // the backend payload, and reducer math like UPDATE_PRODUCT_ITEM_QUANTITY) stays
    // percentage-based exactly as before — "amount" mode just converts the entered
    // rupee amount to its equivalent percentage before it's stored.
    const [discountMode, setDiscountMode] = useState<"amount" | "percentage">("amount");
    const [scanChecked, setScanChecked] = useState(false);
    const [isValue, setIsValue] = useState(false);
    const [openSelect, setOpenSelect] = useState(false)
    // Local UI state for the (former antd AutoComplete) suggestion dropdowns.
    const [showMobileOpts, setShowMobileOpts] = useState(false);
    const [showCustomerNameOpts, setShowCustomerNameOpts] = useState(false);
    const [showProductOpts, setShowProductOpts] = useState(false);

    // The pending row's product-no dropdown is rendered with `position: fixed`
    // (computed from this ref) instead of `absolute`, so it isn't clipped by the
    // items table's scrollable/rounded-corner wrapper. Recomputed on scroll/resize
    // while open so it stays anchored under the input.
    const productFieldRef = useRef<HTMLDivElement>(null);
    const [productDropdownRect, setProductDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);

    useEffect(() => {
        if (!showProductOpts) return;
        const updateRect = () => {
            const el = productFieldRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            setProductDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
        };
        updateRect();
        window.addEventListener("scroll", updateRect, true);
        window.addEventListener("resize", updateRect);
        return () => {
            window.removeEventListener("scroll", updateRect, true);
            window.removeEventListener("resize", updateRect);
        };
    }, [showProductOpts]);

    useEffect(() => {
        if (availableCount) {
            setCreateBilOnload({ ...createBillData, quantity: availableCount })
        }
    }, [availableCount])

    const clearSelectData = () => {
        dispatch(clearSelectAction())
    }

    const createBillApi = () => {
        // Summary now previews the pending (searched-but-not-yet-"Add item"ed) row
        // live, so it visually looks committed — commit it for real before saving,
        // using the exact same commit path "Add item" already uses, so Save
        // includes every row currently shown in the Items table.
        if (hasPendingItem) {
            addProductList();
        }
        if (createBillData?.customerName == ""
            || createBillData?.mobileNumber == ""
            || createBillData?.mobileNumber == null
            || !isValidMobileNumber(createBillData?.mobileNumber)
            || createBillData?.paymentMethodRefId == null
            || createBillData?.billedByEmployeeId == null
            || createBillData?.purchaseDate == ""
            || createBillData?.purchaseDate == null
            || productItemListData == undefined
            || productList.length == 0
            || productList.includes(null, undefined)) {
            setValidation(true)
            window.scroll({ top: 0, behavior: 'smooth' })
            addOneProductToast()
        } else {
            setSpinnerLoad(true)
            // `amount`/`totalAmount` above are render-scoped locals that only get
            // (re)computed when itemsCard() runs during a render pass. addProductList()
            // just mutated `productList`/`totalDiscount` synchronously via .push() in
            // this same click handler, without an intervening render — so those stale
            // closured values would silently drop the just-committed pending item from
            // the payload. Recompute fresh from the (now up to date) arrays instead.
            const freshAmount = (productList || []).reduce((sum: number, item: any) => sum + (parseFloat(item?.total) || 0), 0);
            const freshDiscount = (totalDiscount || []).reduce((sum: number, v: any) => sum + (parseFloat(v?.discount) || 0), 0);
            const freshRoundOff = createBillData?.roundOff == null ? 0 : Number(createBillData.roundOff);
            const freshTotalAmount = computeRoundedTotal(freshAmount, freshRoundOff);
            const selectedPaymentMethod = (paymentList || []).find((item: any) => String(item?.id) === String(createBillData?.paymentMethodRefId));
            // Credit is now applied/validated server-side on both create AND
            // edit (see BillingService.saveBillingDetails_v2's unified credit
            // block) — this client-side convenience check applies equally to
            // both. On edit, customerCreditLimit/customerCreditBalance are
            // populated by the edit-mode initializer below (it seeds the
            // customer search so the same suggestion-list plumbing the
            // create flow uses fills these in), so this no longer wrongly
            // reads 0 on every edit.
            if (selectedPaymentMethod?.name === "Credit") {
                const availableCredit = Number(createBillData?.customerCreditLimit || 0) - Number(createBillData?.customerCreditBalance || 0);
                if (Number(freshTotalAmount) > availableCredit) {
                    setSpinnerLoad(false);
                    toast.error(`Exceeds available credit (₹${availableCredit.toLocaleString("en-IN")})`);
                    return;
                }
            }
            const invalidQtyItem = (productList || []).find((item: any) => getQuantityError(item));
            if (invalidQtyItem) {
                setSpinnerLoad(false);
                toast.error(`${invalidQtyItem.productName}: ${getQuantityError(invalidQtyItem)}`);
                return;
            }
            // Validate bill item quantities against sales return quantities
            if (locationProps?.isEdit == 1 && returnedQtyMap.size > 0) {
                for (const item of productList) {
                    if (item?.productId && returnedQtyMap.has(item.productId)) {
                        const minQty = returnedQtyMap.get(item.productId)!;
                        if (Number(item.quantity) < minQty) {
                            setSpinnerLoad(false);
                            toast.error(`Quantity cannot be less than the returned quantity (${minQty}) for ${item.productName}.`);
                            return;
                        }
                    }
                }
            }
            let payload = {
                billingId: createBillData?.billingId,
                // The customer already mapped to this bill (Edit Bill) or to
                // the picked suggestion/matched number (Create Bill) — lets
                // the backend update/target that exact customer record
                // instead of re-resolving by mobile number.
                customerId: createBillData?.customerId || null,
                billTypeId: 24,
                customerName: createBillData?.customerName,
                mobileNumber: createBillData?.mobileNumber,
                paymentMethodRefId: createBillData?.paymentMethodRefId,
                purchaseDate: createBillData?.purchaseDate,
                billedByEmployeeId: createBillData?.billedByEmployeeId || null,
                productItemId: productItemListData,
                amount: parseFloat(String(freshAmount)).toFixed(2),
                gst: 0,
                discount: parseFloat(String(freshDiscount)).toFixed(2),
                roundOff: freshRoundOff.toFixed(2),
                totalAmount: freshTotalAmount
            }
           
            dispatch(createBillV2Action(payload))
            setCreateBilOnload(true);
        }
    }

    const backToBill = () => {
        clearData()
    }

    //for create or edit billing senario
    useEffect(() => {
        if (createBilOnload == true && createBillReducerOnload_V2 == false) {
            setCreateBilOnload(false);
            setSpinnerLoad(false);
            // createBilling_v2 returns a bare string either way — the exact success
            // message on success, or an error message (e.g. "Invalid id",
            // "Mobile Number Already Exist") on failure. Only leave the screen on
            // the former; on failure, stay put and surface the error instead.
            const response = createBillReducerData_V2?.createBilling_v2;
            if (response === AppConstant?.billingSavedSuccessfully) {
                toast.success(locationProps?.isEdit == 1 ? "Customer Bill updated successfully" : "Customer Bill added successfully");
                backToBill()
                setValidation(false)
            } else if (response) {
                toast.error(response);
            }
        }
    }, [createBilOnload, createBillReducerOnload_V2])

    const getReferenceDataApi = (ref: any) => {
        let payload: any = {
            referenceGroupId: ref
        }
        dispatch(getReferenceDataAction(payload));
    }

    // Native date input needs a YYYY-MM-DD string; also cap selection at today.
    const todayStr = new Date().toISOString().slice(0, 10);

    const clearData = () => {
        amount = 0;
        gst = 0;
        cgst = 0;
        sgst = 0;
        totalAmount = 0;
        dispatch(clearProductItemListAction());
        navigate("/billing")
    }

    // Clears the matched-customer selection — name, mobile number, lock
    // state, and search/suggestion state — so the user can search fresh by
    // either field. Used both by the clear (X) button shown once a customer
    // is selected, and by backspacing either field fully while still typing.
    const clearCustomerSelection = () => {
        setCreateBillData({ ...createBillData, customerName: "", mobileNumber: "", customerId: null, customerCreditLimit: null, customerCreditBalance: null });
        setCustomerSelected(false);
        setSearchnumber("");
        setShowCustomerNameOpts(false);
        setShowMobileOpts(false);
    };

    // Reset — same clear-everything as Cancel (clearData) but stays on this
    // screen instead of navigating to /billing, so a new bill can be started
    // right away. No new dispatch: reuses clearProductItemListAction, the same
    // action Cancel/backToBill already dispatches to empty the redux-held
    // productList/productItemListData/totalDiscount arrays.
    const resetForm = () => {
        amount = 0;
        gst = 0;
        cgst = 0;
        sgst = 0;
        totalAmount = 0;
        dispatch(clearProductItemListAction());
        setCreateBillData(deepCopyFunction(createBillTemp));
        setValidation(false);
        setCustomerSelected(false);
        setSearchnumber("");
        setScanChecked(false);
        setAvailableCount(null);
        setProductListArray([]);
        setDiscountEditIndex(null);
        setQuantityEditIndex(null);
        setDiscountEditData(null);
    }
    const getGstAndAmount = () => {
        totalAmount = amount
    }

    const dispatch = useDispatch();

    // Fetch employee dropdown list on component mount
    useEffect(() => {
        dispatch(employeeDropDownListAction());
    }, [dispatch]);

    const [getBillItemOnload, setGetBillItemOnload] = useState(false);

    const getCustomerListApi = () => {
        let payload = {
            searchText: searchNumber,
            sortBy: 0,
            limit: 0,
            offset: 0
        }
        dispatch(getCustomerListAction(payload));
        setCustomerListLoad(true);
    };

    useEffect(() => {
        if (customerListLoad && !customerListOnload) {
            setMobileNumberList(customerListData?.getCustomerList?.customerDetails ? customerListData?.getCustomerList?.customerDetails : []);
            setCustomerListLoad(false);
        }
    }, [customerListLoad, customerListOnload])

    useEffect(() => {
        // Typing a full matching mobile number is equivalent to picking it from
        // the Mobile Number suggestions — both fields lock.
        //
        // Guarded to only run while not already selected: typing in Customer
        // Name also re-fetches mobilenumberList (via searchNumber), and
        // without this guard every keystroke while the mobile number field
        // still held a stale-but-matching value would re-run this effect.
        if (createBillData?.mobileNumber.length == 10) {
            if (!customerSelected && isArrayNotEmpty(mobilenumberList)) {
                let customerName = mobilenumberList?.find((data: any) => data.mobileNumber == createBillData.mobileNumber);
                if (customerName) {
                    setCreateBillData({ ...createBillData, customerName: customerName?.customerName, customerId: customerName?.customerDetailId })
                    setCustomerSelected(true);
                }
            }
        }
        else if (createBillData?.mobileNumber == "" || createBillData?.mobileNumber == null) {
            setCustomerSelected(false);
        }
    }, [createBillData?.mobileNumber, mobilenumberList, customerSelected])

    useEffect(() => {
        // Drives suggestions for both Customer Name and Mobile Number fields —
        // searchNumber is set from either field's onChange, and the backend
        // getCustomerList query already matches on name OR mobile number.
        if (searchNumber) {
            getCustomerListApi();
        }
    }, [searchNumber])

    const productItemApi = (username: any) => {
        if (username && scanChecked == false) {
            let payload = {
                productId: username ? username.trim() : null,
                isScanned: AppConstant.zero
            }
            dispatch(getProductItemAction(payload))
            setGetBillItemOnload(true)
        }
        else if (createBillData?.items) {
            if ((createBillData?.items).includes("ID-") && scanChecked == true) {
                let payload = {
                    productId: createBillData?.items ? (createBillData?.items).trim() : null,
                    isScanned: AppConstant.one
                }
                dispatch(getProductItemAction(payload))
                setGetBillItemOnload(true)
            } else {
                setCreateBillData({ ...createBillData, items: "", individualDiscount: "" })
                toast.warning("Invalid Product Id")
            }
        }
    }

    useEffect(() => {
        getReferenceDataApi(3)
        getReferenceDataApi(4)
        dispatch(employeeDropDownListAction() as any)
    }, [])

    const [quantityValue, setQuantityValue] = useState<any>();
    useEffect(() => {
        if (getBillItemOnload == true && productItemListOnload == false) {
            if (typeof productItemList?.getProductItem != "string") {
                {
                    (productItemList?.getProductItem || []).map((item: any) => {
                        if (item?.isMultipleRecords != 0) {
                            setIsMultipleRecords(true)
                        } else {
                            setIsMultipleRecords(false)
                            setQuantityValue(item?.availableCount)
                        }
                    })
                }
            }
            else {
                toast.warning(productItemList?.getProductItem);
            }
        }
    }, [getBillItemOnload, productItemListOnload])

    const addProductList = () => {
        if (getBillItemOnload == true && productItemListOnload == false) {
            setAvailableCount(null)
            if (typeof productItemList?.getProductItem != "string") {
                setOpenSelect(false)
                {
                    (productListArray || []).map((item: any) => {
                        // Same product can be added as more than one line (e.g. different
                        // discounts per batch) — the invoice/receipt merge duplicate
                        // productIds back into one line at display time. availableCount
                        // from the search API reflects total stock, unaware of quantity
                        // already staged for this same product elsewhere in productList,
                        // so that has to be subtracted here to avoid overselling.
                        const alreadyAddedQty = (productList || [])
                            .filter((x: any) => x?.productId == item?.productId)
                            .reduce((sum: number, x: any) => sum + (Number(x?.quantity) || 0), 0);
                        const requestedQty = createBillData?.quantity != "" ? Number(createBillData?.quantity) : 1;
                        const withinStock = item?.isMultipleRecords == 0
                            ? (alreadyAddedQty + requestedQty) <= item?.availableCount
                            : true;
                        if ((item?.isMultipleRecords == 0 && withinStock) || (item?.isMultipleRecords == 1)) {
                            productList?.push(item)
                            let mrp = parseFloat(item?.mrp) * (createBillData?.quantity != "" ? Number(createBillData?.quantity) : 1);
                            const enteredDiscount = createBillData?.individualDiscount ? Number(createBillData.individualDiscount) : 0;
                            const discount: any = discountMode === "amount" ? enteredDiscount : mrp * (enteredDiscount / 100);
                            const discountPct = mrp > 0 ? (discount / mrp) * 100 : 0;
                            let obj = {
                                billingItemId: "",
                                productItemId: item?.productItemId,
                                discountPercentage: parseFloat(discountPct.toFixed(4)),
                                quantity: createBillData?.quantity != "" ? Number(createBillData?.quantity) : 0,
                                amount: item?.mrp,
                                isMultipleRecords: item?.isMultipleRecords
                            }
                            productItemListData?.push(obj)

                            let discountData = {
                                productItemId: item?.productItemId,
                                discount: discount
                            }
                            totalDiscount.push(discountData);
                            item.total = mrp - parseFloat(discount);
                            item.discount = parseFloat(discountPct.toFixed(2));
                            item.quantity = createBillData?.quantity != "" ? createBillData?.quantity : 1;
                            setCreateBillData({ ...createBillData, items: "", individualDiscount: "", quantity: "", discount: parseInt(discount) })
                            setGetBillItemOnload(false)
                            setProductListArray([]);
                            setIsMultipleRecords(true)
                            clearSelectData()
                        } else {
                            setProductListArray([]);
                            setCreateBillData({ ...createBillData, items: "", individualDiscount: "", quantity: "" })
                            setIsMultipleRecords(true)
                            toast.warning("Product is out of stack");
                        }
                    })
                }
            } else {
                setCreateBillData({ ...createBillData, items: "", individualDiscount: "", quantity: "" })
                setIsMultipleRecords(true)
                toast.warning(productItemList?.getProductItem)
            }
        }
    }
    const getInvoiceDetailsApi = (item: any) => {
        let payload = {
            invoiceId: item?.invoiceId
        }
        dispatch(getInvoiceDetailsAction(payload))
        setGetInvoiceDetailsload(true);
    }
    // For edit senario in billing product list data
    useEffect(() => {
        if (getInvoiceDetailsload && !getInvoiceDetailsOnload) {
            setGetInvoiceDetailsload(false);
            // Backend returns an error string (not an array) when the invoice
            // has no matching line items — guard the same way productItemList
            // already does elsewhere in this file, instead of crashing .map().
            if (!Array.isArray(getInvoiceDetailsData?.getInvoiceDetails)) {
                if (typeof getInvoiceDetailsData?.getInvoiceDetails === "string") {
                    toast.warning(getInvoiceDetailsData.getInvoiceDetails);
                }
                return;
            }
            getInvoiceDetailsData?.getInvoiceDetails.map((item: any) => {
                let mrp = parseFloat(item?.mrp) * (item?.itemsCount);
                const discount: any = (mrp * ((item?.discountPercentage ? item?.discountPercentage : 0) / 100))
                // item?.availableCount is current remaining stock (excludes this
                // line's own already-billed quantity, since that's already deducted
                // via stockTrack.soldCount) — add itemsCount back so the qty input's
                // max-clamp (see item?.availableCount usage below) lets editing go
                // back up to the original ceiling, not just the amount already billed.
                let obj = {
                    availableCount: (Number(item?.availableCount) || 0) + Number(item?.itemsCount || 0),
                    discount: parseFloat(item?.discountPercentage),
                    isMultipleRecords: item?.isMultipleRecords,
                    mrp: item?.mrp,
                    productDetailsId: item?.productDetailsId,
                    productId: item?.productId,
                    productItemId: item?.productItemId,
                    productName: item?.productName,
                    quantity: item?.itemsCount,
                    total: parseFloat((mrp - parseFloat(discount)).toFixed(2))
                };

                let discountData = {
                    productItemId: item?.productItemId,
                    discount: discount
                };

                totalDiscount.push(discountData);

                productList?.push(obj)
                let obj2 = {
                    billingItemId: item?.billingItemId,
                    productItemId: item?.productItemId,
                    discountPercentage: parseFloat(item?.discountPercentage),
                    quantity: Number(item?.itemsCount),
                    amount: item?.mrp,
                    isMultipleRecords: item?.isMultipleRecords
                }
                productItemListData?.push(obj2)
            })
        }
    }, [getInvoiceDetailsload, getInvoiceDetailsOnload])

    // For edit billing locationProps data
    useEffect(() => {
        if (locationProps && locationProps?.isEdit == 1) {
            setCreateBillData({
                billingId: locationProps?.data?.billingId,
                customerId: locationProps?.data?.customerDetailId || null,
                billTypeId: null,
                customerName: locationProps?.data?.customerName,
                mobileNumber: locationProps?.data?.mobileNumberList,
                paymentMethodRefId: locationProps?.data?.paymentMethodRefId ? locationProps?.data?.paymentMethodRefId.toString() : null,
                purchaseDate: locationProps?.data?.date ? locationProps?.data?.date : "",
                billedByEmployeeId: locationProps?.data?.billedByEmployeeId || null,
                productItemId: null,
                individualAmount: null,
                gst: null,
                individualDiscount: null,
                totalAmount: locationProps?.data?.totalamount,
                roundOff: locationProps?.data?.roundOff,
                items: null,
                quantity: ""
            })
            // Bill already has a customer — Customer Name/Mobile Number load
            // disabled with a clear (X) icon, same as after picking a
            // suggestion in Create Bill. isNilBill forces disabled regardless
            // of this for the shared walk-in "Nil" customer, where editing
            // isn't offered at all.
            setCustomerSelected(true);
            // Seeds the same customer-search plumbing the create flow's
            // suggestion dropdown uses, purely so the effect below can find
            // this customer's creditLimit/creditBalance in the results (the
            // billing-list row locationProps.data comes from doesn't carry
            // them) — doesn't open the suggestion dropdown itself, that's
            // gated separately by showCustomerNameOpts/showMobileOpts.
            setSearchnumber(locationProps?.data?.mobileNumber);
            // Stepping to the previous/next bill re-runs this effect without
            // remounting, so drop the outgoing bill's lines first — the invoice
            // effect below appends into these same reducer arrays, and without
            // the reset the two bills' items would pile up. Emptied in place
            // (rather than via clearProductItemListAction, which resets the
            // whole reducer slice and would wipe the payment/employee reference
            // data this screen only fetches on mount).
            resetBillLineState();
            getInvoiceDetailsApi(locationProps.data)
            // Same lookup salesReturn.tsx uses (billingId is this bill's uniqueKey) —
            // powers the read-only "Returned items" panel below the Items table.
            dispatch(getSalesReturnBillDetailsAction({ invoiceId: locationProps?.data?.billingId }))
        }
    }, [locationProps])

    // Same in-place mutation style the invoice effect above already uses on
    // these arrays.
    function resetBillLineState() {
        if (Array.isArray(productList)) productList.length = 0;
        if (Array.isArray(productItemListData)) productItemListData.length = 0;
        if (Array.isArray(totalDiscount)) totalDiscount.length = 0;
        setProductListArray([]);
        setDiscountEditIndex(null);
        setDiscountEditData(null);
        setQuantityEditIndex(null);
    }

    // Prev/Next bill on the Edit screen. Steps by invoice NUMBER, independent
    // of whatever search/payment filter the list screen was showing — same
    // behavior as the View Billing sheet's navigator. Only meaningful in edit
    // mode — a brand new bill has no siblings.
    const listFilter = locationProps?.listFilter;
    const goToBill = (row: any) => {
        if (!row?.invoiceId) return;
        // replace: leaving the screen still returns to the billing list rather
        // than walking back through every bill stepped through.
        navigate("/create/bill", { state: { data: row, isEdit: 1, listFilter }, replace: true });
    };
    const billNavigation = useBillNavigation({
        currentInvoiceId: locationProps?.isEdit == 1 ? locationProps?.data?.invoiceId : null,
        probeResult: billNavigationRow,
        probeLoading: billNavigationRowOnload,
        onProbe: (invoiceId: string) => dispatch(getBillNavigationRowAction({ searchText: invoiceId, paymentFilter: "", limit: 1, offset: 0 })),
        onSelect: goToBill,
        onExhausted: (direction) => toast.info(direction === "prev" ? "No earlier bill found nearby." : "No later bill found nearby."),
    })

    // Populates customerCreditLimit/customerCreditBalance during edit, once
    // the search seeded above returns this customer — needed so the
    // available-credit check/indicator (createBillApi(), customerAndSummarySection())
    // works the same whether creating or editing a Credit bill.
    useEffect(() => {
        if (locationProps?.isEdit == 1 && createBillData?.customerId && isArrayNotEmpty(mobilenumberList)) {
            let match = mobilenumberList.find((data: any) => String(data?.customerDetailId) === String(createBillData.customerId));
            if (match && (createBillData?.customerCreditLimit !== match.creditLimit || createBillData?.customerCreditBalance !== match.creditBalance)) {
                setCreateBillData({ ...createBillData, customerCreditLimit: match.creditLimit, customerCreditBalance: match.creditBalance });
            }
        }
    }, [locationProps, mobilenumberList, createBillData?.customerId])

    const handleKeyPress = (e: any) => {
        const currentValue = e.target.value;
        if (e.keyCode === 8 || e.keyCode === 37 || e.keyCode === 39 || e.keyCode === 46 || e.keyCode === 9 || e.keyCode === 17 || e.keyCode === 65) {
            return
        }
        if ((e.keyCode >= 48 && e.keyCode <= 57) || e.keyCode == 190) {
            const numberValue = parseFloat(currentValue);
            if ((discountMode === "percentage" && numberValue >= 100) || currentValue.includes('.')) {
                e.preventDefault();
            }
        } else {
            e.preventDefault();
        }
        if (e.key == "Enter") {
            dispatch(updateProductListAction(discountEditData))
            setDiscountEditIndex(null)
            setDiscountEditData(null)
        }
    }

    const handleOnBlur = () => {
        dispatch(updateProductListAction(discountEditData))
        setDiscountEditIndex(null)
        setDiscountEditData(null)
    }

    const discountOnchange = (rawValue: any, item: any) => {
        let mrp = parseFloat(item?.mrp) * (item?.quantity);
        const rawDiscount: any = discountMode === "amount" ? Number(rawValue) : mrp * ((rawValue) / 100);
        const discountData: any = Math.min(Math.max(rawDiscount, 0), mrp);
        const discountPct = mrp > 0 ? (discountData / mrp) * 100 : 0;
        let obj = {
            discount: parseFloat(discountPct.toFixed(2)),
            discountData: discountData,
            total: mrp - parseFloat(discountData),
            productId: item?.productId,
            productItemId: item?.productItemId
        }
        setDiscountEditData(obj)
    }

    const onHandleDiscountEdit = (index: any) => {
        setDiscountEditIndex(index)
        setDiscountEditData(null)
    }

    const removeProductData = (item: any) => {
        setRemovedData(item)
        setRemoveProductmodal(true)
    }

    const productConfirm = () => {
        removeProduct(removedData)
    }

    const productCancel = () => {
        setRemovedData(null)
        setRemoveProductmodal(false)
    }

    const removeProduct = (item: any) => {
        let payload = {
            productId: item?.productId,
            productItemId: item?.productItemId
        }
        dispatch(removedProductListAction(payload))
        setRemovedData(null)
        setRemoveProductmodal(false)
    }

    const addOneProductToast = () => {
        if (productList?.length == 0) {
            toast.warning("Please add atleast one item/product")
        }
    }

    const handleKeyPressItemCount = (e: any) => {
        // Only single printable characters need filtering; named keys (Tab,
        // arrows, Backspace, numpad digits arrive as "0"-"9") and shortcuts pass.
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !/[0-9]/.test(e.key)) {
            e.preventDefault();
        }
    }

    const [isMultipleRecords, setIsMultipleRecords] = useState(true);
    const [value, setValue] = useState<any>();

    const productSearchTimeoutRef = useRef<any>(null);

    const checkingScan = (e: any) => {
        setAvailableCount(null);
        setProductListArray([]);
        setScanChecked(e?.target?.checked)
        setCreateBillData({
            ...createBillData,
            items: ''
        })
    }

    const createBillEnteringProduct = (e: any) => {
        setOpenSelect(true)
        setGetBillItemOnload(true)
        typeof productItemList?.getProductItem !== 'string' &&
            (productItemList?.getProductItem || []).map((option: any) => {
                if (option?.productId == e) {
                    setCreateBillData({
                        ...createBillData,
                        quantity: option?.availableCount == 1 ? 1 : null,
                        multipleCount: option?.availableCount,
                        availableCount: option?.availableCount,
                        items: e
                    })
                    setAvailableCount(option?.availableCount);
                    setProductListArray([option])
                }
            })
    }

    const createBillEnterProduct = (e: any) => {
        if (productSearchTimeoutRef.current) {
            clearTimeout(productSearchTimeoutRef.current);
        }
        productSearchTimeoutRef.current = setTimeout(() => {
            if (e.length <= 0) {
                setAvailableCount(null)
                setCreateBillData({
                    ...createBillData,
                    items: ''
                })
            }
            else {
                productItemApi(e)
            }
            if (openSelect)
                setOpenSelect(true)

        }, 800);
    };

    const productOnBlur = (e: any) => {
        if (e?.target?.value?.length > 0) {
            setScannedId(e?.target?.value)
            productItemApi('')
            setProductApiLoad(true)
            setGetBillItemOnload(true)

        }
    }

    useEffect(() => {
        if (productApiLoad && !productItemListOnload) {
            typeof productItemList?.getProductItem !== 'string' &&
                isArrayNotEmpty(productItemList?.getProductItem) && setCreateBillData({
                    ...createBillData,
                    quantity: productItemList?.getProductItem[0]?.availableCount == 1 ? 1 : null
                })
            setAvailableCount(productItemList?.getProductItem[0]?.availableCount);
            typeof productItemList?.getProductItem !== 'string' &&
                (productItemList?.getProductItem || []).map((option: any) => {
                    if (option?.productId == scannedId) {
                        setProductListArray([option])
                    }
                })
            setProductApiLoad(false)
        }
    }, [productApiLoad, productItemListOnload])

    const scanProductOnChange = (e: any) => {
        if (e.length <= 0) {
            setAvailableCount(null)
            setCreateBillData({
                ...createBillData,
                items: '',
                quantity: null
            })
        }
        else {
            setCreateBillData({
                ...createBillData,
                items: e
            })
        }
    }

    const getQuantityError = (item: any) => {
        const qty = Number(item?.quantity) || 0;
        const returnedMin = returnedQtyMap.get(item?.productId) || 0;
        if (qty < 1) return "Quantity can't be zero";
        if (qty < returnedMin) return `Can't be less than returned qty (${returnedMin})`;
        if (item?.isMultipleRecords == AppConstant?.zero && qty > Number(item?.availableCount)) {
            return `Only ${item?.availableCount} available`;
        }
        return "";
    }

    // Stay in edit mode while the quantity is invalid so the error remains visible.
    const handleQuantityOnBlur = (item: any) => {
        if (!getQuantityError(item)) setQuantityEditIndex(null)
    }

    const onHandleQuantityEdit = (index: any) => {
        setQuantityEditIndex(index)
    }

    const getQuantityFromStore = (productItemId: string) => {
        console.log("productItemListData", productItemListData)
        const matched = productItemListData.find(
            (x: any) => x.productItemId === productItemId
        );
        return matched ? Number(matched.quantity) : "";
    };

    const handleMultipleQuantity = (item: any, value: any) => {
        let payload = {
            productItemId: item.productItemId,
            quantity: Number(value)
        }
        dispatch(updateItemQuantity(payload));
    };

    // Clamp helper for the former antd InputNumber (min/max enforced there).
    const clampNumeric = (raw: string, min: number, max: any) => {
        console.log("raw", raw,"min", min, "max", max)
        if (raw === "") return "";
        let n = Number(raw);
        if (Number.isNaN(n)) return "";
        if (n < min) n = min;
        if (max != null && n > max) n = max;
        return n;
    };

    // ----- Sections (presentation only) -----

    // Walk-in customer entered as "Nil"/"Nil" in both fields (not picked from
    // suggestions, so customerSelected never flips true) — still locks the
    // pair and shows the clear (X) icon, same as a matched customer, so the
    // user can't half-edit a Nil bill without explicitly clearing it first.
    const isNilEntered =
        String(createBillData?.customerName ?? "").trim().toLowerCase() === "nil" &&
        String(createBillData?.mobileNumber ?? "").trim().toLowerCase() === "nil";
    const customerLocked = customerSelected || isNilEntered;
    const currentFieldsAreNil =
        String(createBillData?.customerName ?? "").trim().toLowerCase() === "nil" &&
        String(createBillData?.mobileNumber ?? "").trim().toLowerCase() === "nil";

    const mobileOptions = (createBillData?.mobileNumber != "")
        ? (mobilenumberList || []).filter((o: any) => String(o?.mobileNumber ?? "").includes(String(createBillData?.mobileNumber ?? "")))
        : [];
    const customerNameOptions = (createBillData?.customerName != "")
        ? (mobilenumberList || []).filter((o: any) => String(o?.customerName ?? "").toLowerCase().includes(String(createBillData?.customerName ?? "").toLowerCase()))
        : [];
    const productOptions = typeof productItemList?.getProductItem !== "string" ? (productItemList?.getProductItem || []) : [];

    // Pending ("new item") row preview — mirrors the exact math addProductList()
    // uses when it commits a row (mrp * qty, minus the %, gross → net). Display
    // only; nothing here is dispatched.
    const pendingProduct = productListArray?.[0];
    const pendingQty = createBillData?.quantity && createBillData?.quantity !== "" ? Number(createBillData.quantity) : (pendingProduct ? 1 : 0);
    const pendingMrp = pendingProduct?.mrp ? Number(pendingProduct.mrp) : 0;
    const pendingGross = pendingMrp * pendingQty;
    const pendingDiscountRaw = createBillData?.individualDiscount ? Number(createBillData.individualDiscount) : 0;
    const pendingDiscountAmount = discountMode === "amount" ? pendingDiscountRaw : (pendingGross * pendingDiscountRaw) / 100;
    const pendingLineTotal = pendingGross - pendingDiscountAmount;
    const hasPendingItem = !!pendingProduct && pendingQty > 0;

    const clearPendingRow = () => {
        setProductListArray([]);
        setAvailableCount(null);
        setCreateBillData({ ...createBillData, items: "", individualDiscount: "", quantity: "" });
    }

    const itemsCard = () => {
        return (
            <Card className="mb-4 shadow-sm">
                <CardContent className="p-4">
                    {/* <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-base font-semibold text-foreground">{AppConstant?.items}</h2>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>Line Discount:</span>
                                <RadioGroup
                                    className="grid-flow-col gap-3"
                                    value={discountMode}
                                    onValueChange={(v: any) => setDiscountMode(v)}
                                >
                                    <label className="flex items-center gap-1.5">
                                        <RadioGroupItem value="amount" aria-label="Amount" />
                                        Amount
                                    </label>
                                    <label className="flex items-center gap-1.5">
                                        <RadioGroupItem value="percentage" aria-label="Percentage" />
                                        Percentage
                                    </label>
                                </RadioGroup>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Checkbox
                                    checked={scanChecked}
                                    onCheckedChange={(c: any) => checkingScan({ target: { checked: c === true } })}
                                    aria-label="Scan"
                                />
                                Scan
                            </label>
                            <Button
                                size="sm"
                                disabled={createBillData?.quantity == null || createBillData?.quantity == '' || createBillData?.quantity == undefined}
                                onClick={() => addProductList()}
                            >
                                <Plus className="mr-1.5 h-4 w-4" /> Add item
                            </Button>
                        </div>
                    </div> */}

                    <div className="max-h-[520px] overflow-y-auto rounded-xl border">
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead>S.No</TableHead>
                                    <TableHead>Product no</TableHead>
                                    <TableHead>Product name</TableHead>
                                    <TableHead>Qty</TableHead>
                                    <TableHead>Price</TableHead>
                                    <TableHead>Discount</TableHead>
                                    <TableHead>Line total</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(productList || []).map((item: any, index: any) => {
                                    amount += item?.total;
                                    return (item == undefined) ? null : (
                                        <TableRow key={item?.productItemId ?? index} className="even:bg-muted/30">
                                            <TableCell>{++number}</TableCell>
                                            <TableCell className="font-medium">{item?.productId}</TableCell>
                                            <TableCell>{item?.productName}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {quantityEditIndex == index ? (
                                                         <div className="relative">
                                                             <Input
                                                                 className={`h-8 w-20 ${getQuantityError(item) ? "border-destructive focus-visible:ring-destructive" : ""}`}
                                                                 autoFocus
                                                                 onFocus={(e) => {
                                                                     const input = e.target;
                                                                     requestAnimationFrame(() => input.select());
                                                                 }}
                                                                 onMouseUp={(e) => e.preventDefault()}
                                                                 inputMode="numeric"
                                                                 onKeyDown={(e) => handleKeyPressItemCount(e)}
                                                                 value={getQuantityFromStore(item.productItemId) || ""}
                                                                 onBlur={() => handleQuantityOnBlur(item)}
                                                                 onChange={(e) => handleMultipleQuantity(item, e.target.value)}
                                                             />
                                                             {getQuantityError(item) && (
                                                                 <span className="absolute left-0 top-full z-10 mt-0.5 whitespace-nowrap text-xs text-destructive">{getQuantityError(item)}</span>
                                                             )}
                                                         </div>
                                                    ) : (
                                                        item?.quantity
                                                    )}
                                                    {quantityEditIndex != index && !isRestrictedEdit && (
                                                        <button type="button" onClick={() => onHandleQuantityEdit(index)} aria-label="Edit quantity">
                                                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                                        </button>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>₹{item?.mrp}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {discountEditIndex == index ? (
                                                        <Input
                                                            className="h-8 w-20"
                                                            autoFocus
                                                            placeholder={discountMode === "amount" ? "₹" : "%"}
                                                            onKeyDown={(e) => handleKeyPress(e)}
                                                            onBlur={() => handleOnBlur()}
                                                            onChange={(e) => { discountOnchange(e.target.value, item) }}
                                                        />
                                                    ) : (
                                                        item.discount ? `${item.discount}%` : "-"
                                                    )}
                                                    {discountEditIndex != index && !isRestrictedEdit && (
                                                        <button type="button" onClick={() => onHandleDiscountEdit(index)} aria-label="Edit discount">
                                                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                                        </button>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-medium tabular-nums">₹{Number(item.total).toFixed(2)}</TableCell>
                                            <TableCell>
                                                {!isRestrictedEdit && (
                                                    <button
                                                        type="button"
                                                        disabled={returnedQtyMap.has(item.productId)}
                                                        onClick={() => removeProductData(item)}
                                                        aria-label="Remove product"
                                                        title={returnedQtyMap.has(item.productId) ? "Cannot remove — this product has sales returns" : "Remove product"}
                                                    >
                                                        <Trash2 className={`h-4 w-4 ${returnedQtyMap.has(item.productId) ? "text-muted-foreground/40" : "text-destructive"}`} />
                                                    </button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}

                                {/* Pending row — search/scan a product to add it to the bill.
                                    Hidden entirely once isRestrictedEdit: this bill's line items
                                    are locked, so there's nothing to search/add. */}
                                {!isRestrictedEdit && (
                                <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell className="text-muted-foreground">{productList.length + 1}</TableCell>
                                    <TableCell>
                                        {scanChecked ? (
                                            <Input
                                                className="h-9"
                                                value={createBillData?.items ? createBillData?.items : ''}
                                                placeholder="Scan/Enter Product ID"
                                                onChange={(e) => scanProductOnChange(e.target.value)}
                                                onBlur={(e) => productOnBlur(e)}
                                            />
                                        ) : (
                                            <div className="relative" ref={productFieldRef}>
                                                <Input
                                                    className="h-9 pr-8"
                                                    value={createBillData?.items ? createBillData?.items : ""}
                                                    placeholder="Search product no…"
                                                    onChange={(e) => { setCreateBillData({ ...createBillData, items: e.target.value }); createBillEnterProduct(e.target.value); setShowProductOpts(true); }}
                                                    onFocus={() => setShowProductOpts(true)}
                                                    onBlur={() => setTimeout(() => setShowProductOpts(false), 150)}
                                                />
                                                <Search className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                {/* Rendered with position:fixed (not absolute) so the items table's
                                                    scroll wrapper can't clip it. */}
                                                {showProductOpts && productOptions.length > 0 && productDropdownRect && (
                                                    <ul
                                                        className="fixed z-50 max-h-64 list-none overflow-auto rounded-md border bg-popover py-1 shadow-md"
                                                        style={{ top: productDropdownRect.top, left: productDropdownRect.left, width: Math.max(productDropdownRect.width, 288) }}
                                                    >
                                                        {productOptions.map((option: any) => (
                                                            <li key={option?.productId}>
                                                                <button
                                                                    type="button"
                                                                    className="block w-full px-3 py-2 text-left hover:bg-muted"
                                                                    onMouseDown={(e) => { e.preventDefault(); setCreateBillData({ ...createBillData, items: option?.productId }); createBillEnteringProduct(option?.productId); setShowProductOpts(false); }}
                                                                >
                                                                    <div className="text-sm font-medium">{option?.productId} · {option?.productName}</div>
                                                                    <div className="text-xs text-muted-foreground">₹{option?.mrp} · {option?.availableCount} in stock</div>
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{pendingProduct?.productName ?? "—"}</TableCell>
                                    <TableCell>
                                        <Input
                                            className="h-9 w-20"
                                            inputMode="numeric"
                                            disabled={availableCount == undefined || availableCount == null}
                                            onKeyDown={(e) => handleKeyPressItemCount(e)}
                                            value={createBillData?.quantity == 1 ? 1 : createBillData?.quantity}
                                            onChange={(e) => setCreateBillData({ ...createBillData, quantity: clampNumeric(e.target.value, 0, availableCount) })}
                                        />
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">₹{pendingMrp.toFixed(2)}</TableCell>
                                    <TableCell>
                                        <Input
                                            className="h-9 w-20"
                                            inputMode="numeric"
                                            placeholder={discountMode === "amount" ? "₹" : "%"}
                                            disabled={(createBillData?.items == "" || createBillData?.items == undefined) ? true : false}
                                            onKeyDown={(e) => handleKeyPressItemCount(e)}
                                            value={createBillData?.individualDiscount ?? ""}
                                            onChange={(e) => setCreateBillData({ ...createBillData, individualDiscount: clampNumeric(e.target.value, 0, discountMode === "amount" ? pendingGross : 100) })}
                                        />
                                    </TableCell>
                                    <TableCell className="font-medium tabular-nums">₹{pendingLineTotal.toFixed(2)}</TableCell>
                                    <TableCell>
                                        <button type="button" onClick={() => clearPendingRow()} aria-label="Clear pending item">
                                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                                        </button>
                                    </TableCell>
                                </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        )
    }

    // Display-only preview of the shared round-off rule, so the pending
    // (not-yet-added) row can be previewed in the summary without touching the
    // outer `amount`/`totalAmount` vars that createBillApi() reads on Save —
    // those must keep reflecting only items actually committed to productList.
    const computeGrandTotalDisplay = (rawTotal: number, roundOff: any) => computeRoundedTotal(rawTotal, roundOff);

    const customerAndSummarySection = () => {
        getGstAndAmount()
        const committedDiscounts = parseFloat(totalDiscount.reduce((a: any, v: any) => a = a + v.discount, 0)) || 0;
        const lineDiscounts = committedDiscounts + (hasPendingItem ? pendingDiscountAmount : 0);
        const subtotal = amount + committedDiscounts + (hasPendingItem ? pendingGross : 0);
        const overallDiscount = createBillData?.roundOff ? Number(createBillData.roundOff) : 0;
        const grandTotal = computeGrandTotalDisplay(amount + (hasPendingItem ? pendingLineTotal : 0), createBillData?.roundOff);
        const selectedPaymentMethod = (paymentList || []).find((item: any) => String(item?.id) === String(createBillData?.paymentMethodRefId));
        // Credit is now applied/validated on edit too — see the matching
        // guard in createBillApi(). customerCreditLimit/customerCreditBalance
        // are kept populated during edit by the effect below that piggybacks
        // on the customer-search results.
        const isPayingByCredit = selectedPaymentMethod?.name === "Credit";
        const customerAvailableCredit = Number(createBillData?.customerCreditLimit || 0) - Number(createBillData?.customerCreditBalance || 0);

        return (
            <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <FormSection columns={3} className="lg:col-span-2">
                    {/* Customer Name */}
                    <FieldRow
                        label={AppConstant?.customerName}
                        required
                        error={
                            (validation && (createBillData?.customerName == null || createBillData?.customerName == ""))
                                ? "Customer Name is required"
                                : (!customerSelected && createBillData?.customerName != "" && (createBillData?.customerName).trim() === "")
                                    ? "Invalid Name"
                                    : undefined
                        }
                    >
                        <div className="relative">
                            <Input
                                value={createBillData?.customerName}
                                disabled={(isNilBill && currentFieldsAreNil) || customerLocked}
                                placeholder="Customer name"
                                className={(customerLocked || currentFieldsAreNil) ? "pr-8" : undefined}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "") {
                                        clearCustomerSelection();
                                    } else {
                                        setCreateBillData({ ...createBillData, customerName: value });
                                        setSearchnumber(value);
                                        setShowCustomerNameOpts(true);
                                    }
                                }}
                                onFocus={() => setShowCustomerNameOpts(true)}
                                onBlur={() => setTimeout(() => setShowCustomerNameOpts(false), 150)}
                            />
                            {(customerLocked || currentFieldsAreNil) && (
                                <button
                                    type="button"
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    aria-label="Clear selected customer"
                                    onClick={clearCustomerSelection}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                            {showCustomerNameOpts && customerNameOptions.length > 0 && (
                                <ul className="absolute z-50 mt-1 max-h-48 w-full list-none overflow-auto rounded-md border bg-popover py-1 shadow-md">
                                    {customerNameOptions.map((o: any, idx: number) => (
                                        <li key={`${o?.mobileNumber ?? "cust"}-${idx}`}>
                                            <button
                                                type="button"
                                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                                                onMouseDown={(e) => { e.preventDefault(); setCreateBillData({ ...createBillData, customerName: o?.customerName, mobileNumber: o?.mobileNumber, customerId: o?.customerDetailId, customerCreditLimit: o?.creditLimit, customerCreditBalance: o?.creditBalance }); setShowCustomerNameOpts(false); setCustomerSelected(true); }}
                                            >
                                                <div className="font-medium">{o?.customerName}</div>
                                                <div className="text-xs text-muted-foreground">{o?.mobileNumber}</div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </FieldRow>

                    {/* Mobile — former antd AutoComplete */}
                    <FieldRow
                        label={AppConstant?.mobile}
                        required
                        error={
                            (validation && (createBillData?.mobileNumber == null || createBillData?.mobileNumber == ""))
                                ? "Mobile Number is required"
                                : (createBillData?.mobileNumber != "" && (validation || !customerSelected) && !isValidMobileNumber(createBillData?.mobileNumber))
                                    ? 'Please enter Valid Mobile Number or "Nil"'
                                    : undefined
                        }
                    >
                        <div className="relative">
                            <Input
                                disabled={(isNilBill && currentFieldsAreNil) || customerLocked}
                                value={createBillData?.mobileNumber ? createBillData?.mobileNumber : ""}
                                placeholder="10-digit number"
                                className={(customerLocked || currentFieldsAreNil) ? "pr-8" : undefined}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "") {
                                        clearCustomerSelection();
                                    } else {
                                        setCreateBillData({ ...createBillData, mobileNumber: value });
                                        setSearchnumber(value);
                                        setShowMobileOpts(true);
                                    }
                                }}
                                onFocus={() => setShowMobileOpts(true)}
                                onBlur={() => setTimeout(() => setShowMobileOpts(false), 150)}
                            />
                            {(customerLocked || currentFieldsAreNil) && (
                                <button
                                    type="button"
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    aria-label="Clear selected customer"
                                    onClick={clearCustomerSelection}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                            {showMobileOpts && mobileOptions.length > 0 && (
                                <ul className="absolute z-50 mt-1 max-h-48 w-full list-none overflow-auto rounded-md border bg-popover py-1 shadow-md">
                                    {mobileOptions.map((o: any, idx: number) => (
                                        <li key={`${o?.mobileNumber ?? "mob"}-${idx}`}>
                                            <button
                                                type="button"
                                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                                                onMouseDown={(e) => { e.preventDefault(); setCreateBillData({ ...createBillData, mobileNumber: o?.mobileNumber, customerName: o?.customerName, customerId: o?.customerDetailId, customerCreditLimit: o?.creditLimit, customerCreditBalance: o?.creditBalance }); setShowMobileOpts(false); setCustomerSelected(true); }}
                                            >
                                                <div className="font-medium">{o?.mobileNumber}</div>
                                                <div className="text-xs text-muted-foreground">{o?.customerName}</div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </FieldRow>

                    {/* Payment Method — "Credit" is only offered when the selected
                        customer has a creditLimit > 0; the actual enforcement
                        happens server-side (see CustomerCreditService), this is
                        just a UX convenience. */}
                    <FieldRow
                        label={AppConstant?.paymentMethod}
                        required
                        error={
                            (validation && (createBillData?.paymentMethodRefId == null)) ? "Payment Method is reqired" :
                                (isPayingByCredit && Number(grandTotal) > customerAvailableCredit)
                                    ? `Exceeds available credit (₹${customerAvailableCredit.toLocaleString("en-IN")})`
                                    : undefined
                        }
                        hint={isPayingByCredit ? `Available Credit: ₹${customerAvailableCredit.toLocaleString("en-IN")}` : undefined}
                    >
                        <Select
                            value={createBillData?.paymentMethodRefId != null ? String(createBillData?.paymentMethodRefId) : undefined}
                            onValueChange={(v) => setCreateBillData({ ...createBillData, paymentMethodRefId: v })}
                        >
                            <SelectTrigger><SelectValue placeholder="Choose Payment Method" /></SelectTrigger>
                            <SelectContent>
                                {(paymentList || [])
                                    // On edit, customerCreditLimit is never populated (the
                                    // customer suggestion picker isn't used), so this must not
                                    // hide "Credit" when it's already the bill's own payment
                                    // method — that would break re-selecting the existing value.
                                    .filter((item: any) => item?.name !== "Credit" || Number(createBillData?.customerCreditLimit) > 0 || locationProps?.isEdit == 1)
                                    .map((item: any) => (
                                        <SelectItem key={item?.id} value={String(item?.id)}>{item?.name}</SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </FieldRow>

                    {/* Employee — mandatory, identifies which staff member billed this */}
                    <FieldRow
                        label="Employee"
                        required
                        error={(validation && createBillData?.billedByEmployeeId == null) ? "Employee is required" : undefined}
                    >
                        <Select
                            disabled={isRestrictedEdit}
                            value={createBillData?.billedByEmployeeId != null ? String(createBillData?.billedByEmployeeId) : ""}
                            onValueChange={(v) => setCreateBillData({ ...createBillData, billedByEmployeeId: v || null })}
                        >
                            <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                            <SelectContent>
                                {(employeeList || []).map((item: any) => (
                                    <SelectItem key={item?.employeeId} value={String(item?.employeeId)}>{item?.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FieldRow>

                    {/* Purchase Date — former antd DatePicker → native date input */}
                    <FieldRow
                        label="Purchase Date"
                        required
                        error={(validation && (createBillData?.purchaseDate == "")) ? "Purchase Date is required" : undefined}
                    >
                        <Input
                            type="date"
                            max={todayStr}
                            disabled={isRestrictedEdit}
                            value={createBillData?.purchaseDate ? String(createBillData?.purchaseDate).slice(0, 10) : ""}
                            onChange={(e) => setCreateBillData({ ...createBillData, purchaseDate: e.target.value })}
                        />
                    </FieldRow>

                  

                    {/* Overall discount — the legacy `roundOff` field (label only changed to match this layout). */}
                    <FieldRow label="Overall discount">
                        <Input
                            inputMode="numeric"
                            disabled={isRestrictedEdit}
                            value={createBillData?.roundOff ?? ""}
                            onKeyDown={(e) => handleKeyPressItemCount(e)}
                            onChange={(e) => setCreateBillData({ ...createBillData, roundOff: e.target.value ? parseInt(e.target.value) : null })}
                        />
                    </FieldRow>

                      {/* Bill Type — disabled, Non-GST (billTypeId hardcoded to 24 on submit) */}
                    <FieldRow label={AppConstant?.billType  }>
                        <div className="inline-flex overflow-hidden rounded-lg border">
                            <span aria-disabled className="cursor-not-allowed bg-muted px-4 py-2 text-sm text-muted-foreground">GST</span>
                            <span className="cursor-default bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">Non-GST</span>
                        </div>
                    </FieldRow>
                </FormSection>

                <Card className="shadow-sm">
                    <CardContent className="space-y-3 p-6">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="tabular-nums">₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Line discounts</span>
                            <span className="tabular-nums">− ₹{lineDiscounts.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Overall discount</span>
                            <span className="tabular-nums">− ₹{overallDiscount.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">GST tax</span>
                            <span className="tabular-nums">+ ₹0.00</span>
                        </div>
                        <div className="my-1 border-t" />
                        <div className="flex items-center justify-between">
                            <span className="text-base font-semibold">Grand total</span>
                            <span className="text-lg font-semibold tabular-nums">₹{grandTotal}</span>
                        </div>
                    </CardContent>
                </Card>

                <div className="flex flex-wrap justify-end gap-2 lg:col-span-3">
                    <Button variant="outline" onClick={() => resetForm()}>
                        <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
                    </Button>
                    <Button variant="outline" onClick={() => backToBill()}>{AppConstant?.cancel}</Button>
                    <Button onClick={() => createBillApi()}>
                        <Save className="mr-1.5 h-4 w-4" /> {AppConstant?.save}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4 pt-0">
            <ApparelMenu selectedKey="2" />
            <div className="sticky top-0 z-20 -mx-6 mb-4 flex items-center justify-between gap-3 border-b bg-background px-6 py-2">
                <div className="flex min-w-0 items-center gap-3">
                    <h2 className="text-base font-semibold text-foreground">{AppConstant?.items}</h2>
                    {locationProps?.isEdit == 1 && locationProps?.data?.invoiceId && (
                        <span className="text-sm font-normal text-muted-foreground">Bill No: {locationProps.data.invoiceId}</span>
                    )}
                    {locationProps?.isEdit == 1 && (
                        <div className="ml-2 flex items-center"> 
                            <BillNavigator {...billNavigation} className="h-8" />
                        </div>
                    )}
                    {/* {isRestrictedEdit && (
                        // <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                        //     This bill is not from today — only Customer details and Payment Method can be edited
                        // </span>
                    )} */}
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Line Discount:</span>
                        <RadioGroup
                            className="grid-flow-col gap-3"
                            value={discountMode}
                            onValueChange={(v: any) => setDiscountMode(v)}
                        >
                            <label className="flex items-center gap-1.5">
                                <RadioGroupItem value="amount" aria-label="Amount" />
                                Amount
                            </label>
                            <label className="flex items-center gap-1.5">
                                <RadioGroupItem value="percentage" aria-label="Percentage" />
                                Percentage
                            </label>
                        </RadioGroup>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Checkbox
                            checked={scanChecked}
                            disabled={isRestrictedEdit}
                            onCheckedChange={(c: any) => checkingScan({ target: { checked: c === true } })}
                            aria-label="Scan"
                        />
                        Scan
                    </label>
                    <Button
                        size="sm"
                        className="shrink-0"
                        disabled={isRestrictedEdit || createBillData?.quantity == null || createBillData?.quantity == '' || createBillData?.quantity == undefined}
                        onClick={() => addProductList()}
                    >
                        <Plus className="mr-1.5 h-4 w-4" /> Add item
                    </Button>
                </div>
            </div>
            {itemsCard()}
            {locationProps?.isEdit == 1 && returnedLineItems.length > 0 && (
                <Card className="mb-4 shadow-sm">
                    <CardContent className="p-4">
                        <h3 className="mb-3 text-sm font-semibold text-foreground">Returned items</h3>
                        <div className="rounded-lg border">
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead>Product no</TableHead>
                                        <TableHead>Product name</TableHead>
                                        <TableHead>Qty returned</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {returnedLineItems.map((item: any, index: number) => (
                                        <TableRow key={item?.productId ?? index} className="even:bg-muted/30">
                                            <TableCell className="font-medium">{item?.productId}</TableCell>
                                            <TableCell>{item?.productName}</TableCell>
                                            <TableCell className="tabular-nums">{item?.returnedQty}</TableCell>
                                            <TableCell>{item?.status ? <StatusBadge status={item.status} /> : "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}
            {customerAndSummarySection()}
            <ConfirmDialog
                open={removeProductmodal}
                onOpenChange={(o: boolean) => { if (!o) productCancel(); }}
                title="Are you sure want to remove product ?"
                confirmLabel={AppConstant?.ok}
                cancelLabel={AppConstant?.cancel}
                destructive
                onConfirm={() => productConfirm()}
            />
            <LoadingOverlay loading={spinnerLoad} />
        </div>
    )
}

export default CreateBill;
